// First-run tour — client side of the once-per-account gate.
//
// The six-screen tour (pages/FirstRunTourPage) shows exactly once, right
// after a talent is logged in AND Stripe is complete. The source of truth is
// User.firstRunTourSeenAt on the server (GET /users/:slug carries it; PATCH
// /users/:slug/first-run-tour stamps it), so finishing on the phone means the
// laptop never shows it. NO backfill by design (founder decision): pre-existing
// talents see it once on their next login too.
//
// The session marker exists for one reason: the stamp is fire-and-forget, so
// the Requests page can mount (and re-read the profile) before the PATCH has
// landed. Without it the gate could bounce a talent straight back into the
// tour they just finished. sessionStorage, not localStorage, on purpose — a
// fresh app open re-checks the server, so a lost PATCH simply shows the tour
// again later (silent recovery, never an error).
import api from "../api";

const KEY_PREFIX = "rq_first_run_tour_done_";

export interface FirstRunTourBits {
  slug?: string;
  isStripeOnboarded?: boolean;
  firstRunTourSeenAt?: string | null;
  /** Free tier: false until the bank is connected AND the founder approved. */
  canTakeMoney?: boolean;
}

export function finishedFirstRunTourThisSession(slug: string): boolean {
  try {
    return sessionStorage.getItem(KEY_PREFIX + slug) === "1";
  } catch {
    return false;
  }
}

/** True when the account should be routed into the tour right now. Only an
 *  explicit null triggers it: a server that doesn't know the field yet
 *  (undefined) must never show the tour on every open. Deploy order is
 *  server first, but this makes the client safe either way. */
export function needsFirstRunTour(profile?: FirstRunTourBits | null): boolean {
  if (!profile || !profile.slug) return false;
  if (!profile.isStripeOnboarded || profile.firstRunTourSeenAt !== null) return false;
  // Free tier: the tour opens on "You keep 90%" — it waits for the day the
  // talent can actually be paid (bank connected AND approved).
  if (profile.canTakeMoney === false) return false;
  return !finishedFirstRunTourThisSession(profile.slug);
}

/** Stamp the account. Fire-and-forget: the UI moves on immediately and a
 *  failure is silent (the tour would just show again on a later login). */
export function markFirstRunTourSeen(slug: string): void {
  try {
    sessionStorage.setItem(KEY_PREFIX + slug, "1");
  } catch {
    /* storage unavailable — the server flag still lands below */
  }
  api.patch(`/users/${slug}/first-run-tour`).catch(() => {
    /* silent — see above */
  });
}
